import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { Ability } from 'ember-can';
import EntryModel from 'garaje/models/entry';
import InviteModel from 'garaje/models/invite';
import type AuthzService from 'garaje/services/authz';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';
import { GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST } from 'garaje/utils/roles';
import { Permission } from 'garaje/utils/ui-permissions';
import _intersection from 'lodash/intersection';

import type { GetCanReviewOrReadBlocklistMatchModel } from './security';

const CAN_REVIEW_ROLES = [GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST];

export default class IdScanningAbility extends Ability {
  declare model?: GetCanReviewOrReadBlocklistMatchModel;

  @service declare authz: AuthzService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare state: StateService;

  get canEnable(): boolean {
    return !!this.state.features?.canAccessIdScanning;
  }

  get canManageConfiguration(): boolean {
    return this.authz.hasPermissionAtCurrentLocation(Permission.VISITORS_ID_SCAN_CONFIGURATION_MANAGE);
  }

  get canReview(): boolean {
    if (this.featureFlags.isEnabled('visitors-id-approval-permissions')) {
      const { parent } = this.model ?? {};

      if (parent instanceof EntryModel) return this.canReviewAnEntryApproval;
      if (parent instanceof InviteModel) return this.canReviewAnInviteApproval;

      return this.canReviewAnInviteApproval || this.canReviewAnEntryApproval;
    }

    const { roleNames: roles } = this.currentAdmin;
    return isPresent(_intersection(CAN_REVIEW_ROLES, roles));
  }

  get canReviewAnEntryApproval(): boolean {
    return this.authz.hasPermissionAtCurrentLocation(Permission.VISITORS_ENTRY_ID_SCANNING_APPROVAL_REVIEW);
  }

  get canReviewAnInviteApproval(): boolean {
    return this.authz.hasPermissionAtCurrentLocation(Permission.VISITORS_INVITE_ID_SCANNING_APPROVAL_REVIEW);
  }
}
